import React, { useRef, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Top from '../Top/Top';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import TemplateParser from '../Templates/TemplateParser';
import { offset, scrollTo, addClass, removeClass } from '../../utils/dom';
import { deepClone } from '../../utils/object';
import s from './Page.scss';

const Page = ({ data, location, setup, hashLocation = false }) => {
	if (!data) return null;
	let overlayHeader = false;
	let overlayHeaderInverse = false;
	let marginTop = '0px';

	const pageElement = useRef(null);
	const scrollToHash = () => {
		let imagesLoaded = null;
		const localHashLocation = hashLocation || window.location.hash;

		const allLoaded = () => {
			if (pageElement && pageElement.current) {
				const imgs = pageElement.current.querySelectorAll('img');
				imagesLoaded = [...imgs].length;

				[...imgs].forEach((img) => {
					if (img.complete) imagesLoaded -= 1;
				});
			}

			if (pageElement && pageElement.current && imagesLoaded === 0) {
				// Delay to understand page navigation
				setTimeout(() => {
					const position = offset(document.getElementById(localHashLocation.replace('#', ''))).top - 170;
					scrollTo(500, position);
				}, 300);
			} else {
				setTimeout(allLoaded, 50);
			}
		};

		allLoaded();
	};

	if (data?.content?.sections && data.content.sections.length > 0) {
		let firstSection = data.content.sections[0];
		if (firstSection?.ccBlocksSlider?.content && firstSection.ccBlocksSlider.content.length > 0) {
			firstSection = firstSection.ccBlocksSlider.content[0];
		}
		if (firstSection.ccBlocksPuff && firstSection.ccBlocksPuff?.attrs?.transparentMenu) {
			overlayHeader = true;
			marginTop = setup.breakPoint === 'desktop' ? '-70px' : '-55px';
			if (firstSection.ccBlocksPuff.attrs.dark) {
				overlayHeaderInverse = true;
			}
		}
	}

	useEffect(() => {
		if (hashLocation || window.location.hash !== '') scrollToHash();

		if (overlayHeader) addClass(document.body, 'u_overlayHeader');
		if (overlayHeaderInverse) addClass(document.body, 'u_overlayHeader__inverse');

		return () => {
			removeClass(document.body, ['u_overlayHeader', 'u_overlayHeader__inverse']);
		};
	}, [overlayHeader, overlayHeaderInverse, hashLocation]);

	const pageClasses = [s.page];
	if (data.content?.meta?.default?.tags?.support) pageClasses.push('u_supportPage');

	const pageInnerClasses = [s.page_inner];
	if (data.addMenu) pageInnerClasses.push(s.page_inner__withMenu);

	// Check if page contains core/block
	const skipParts = [];

	return (
		<main role="main" className={location.type === 'START' ? 'u_startPage' : null}>
			<Top setup={setup} pageContent={data.content?.meta?.default} paths={data.paths} />
			<div className={pageClasses.join(' ')} ref={pageElement} style={{ marginTop }}>
				<div className={pageInnerClasses.join(' ')}>
					<ErrorBoundary message="Page error" debugData={data.content}>
						{
							data?.content?.sections && data.content.sections.map((section) => {
								if (Object.keys(section)[0] === 'coreBlock') {
									const whichPart = section.coreBlock.content;
									let partsContent = null;

									data.parts.forEach((part) => {
										if (parseInt(part.meta.id, 10) === parseInt(whichPart, 10)) {
											partsContent = deepClone(part.sections);
											skipParts.push(part.meta.id);
										}
									});

									return partsContent && <TemplateParser sections={partsContent} setup={setup} />;
								}
								return <TemplateParser sections={[section]} setup={setup} />;
							})
						}
						{
							data.parts && data.parts.length > 0 && data.parts.map((section) => {
								if (skipParts.indexOf(section.meta.id) !== -1) return null;
								return <TemplateParser sections={section.sections} setup={setup} />;
							})
						}
					</ErrorBoundary>
				</div>
			</div>
		</main>
	);
};

const mapState = ({ environment, location }, { data }) => ({
	data,
	location,
	hashLocation: environment.hashLocation,
	setup: environment.setup,
});

export default hot(module)(connect(mapState)(Page));
